import React, { useState, useEffect, useRef, useCallback } from 'react';
import { fetchWheels, searchWheelsByImage } from '../api';
import './wheellibrary.css';
import WheelModal from './wheelmodal';
import NewWheelModal from './newwheelmodal';

const WheelLibrary = () => {
  const [wheels, setWheels] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedWheel, setSelectedWheel] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showNewModal, setShowNewModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [failedImages, setFailedImages] = useState(new Set());

  // Image search state
  const [imageSearchResults, setImageSearchResults] = useState(null); // null = not active
  const [imageSearchLoading, setImageSearchLoading] = useState(false);
  const [imageSearchError, setImageSearchError] = useState(null);
  const [imageSearchPreview, setImageSearchPreview] = useState(null);

  const searchTimerRef = useRef(null);
  const imageInputRef = useRef(null);

  const loadWheels = useCallback(async (query = '') => {
    setIsLoading(true);
    try {
      const data = await fetchWheels(query, 1, 100);
      setWheels(data.results || []);
    } catch (error) {
      console.error('Error loading wheels:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadWheels();
  }, [loadWheels]);

  const handleSearchChange = (e) => {
    const q = e.target.value;
    setSearchQuery(q);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      loadWheels(q);
    }, 300);
  };

  const handleWheelClick = (wheel) => {
    setSelectedWheel(wheel);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedWheel(null);
  };

  const handleWheelUpdated = (updatedWheel) => {
    setWheels((prev) =>
      prev.map((w) => (w.id === updatedWheel.id ? updatedWheel : w))
    );
    setSelectedWheel(updatedWheel);
  };

  const handleWheelDeleted = (wheelId) => {
    setWheels((prev) => prev.filter((w) => w.id !== wheelId));
    setShowModal(false);
    setSelectedWheel(null);
  };

  const handleWheelCreated = (newWheel) => {
    setWheels((prev) => [newWheel, ...prev]);
  };

  const getPrimaryImageUrl = (wheel) => {
    if (!wheel.images || wheel.images.length === 0) return null;
    const primary = wheel.images.find((img) => img.is_primary) || wheel.images[0];
    return primary ? primary.image : null;
  };

  // ── Image search ────────────────────────────────────────────────────────

  const handleImageSearchClick = () => {
    imageInputRef.current && imageInputRef.current.click();
  };

  const handleImageSearchFileChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    // Reset input so the same file can be picked again
    e.target.value = '';

    // Show preview
    const previewUrl = URL.createObjectURL(file);
    setImageSearchPreview(previewUrl);
    setImageSearchError(null);
    setImageSearchLoading(true);
    setImageSearchResults(null);

    try {
      const data = await searchWheelsByImage(file, 10);
      setImageSearchResults(data.results || []);
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        'Image search failed. Make sure AWS Bedrock is configured.';
      setImageSearchError(msg);
      setImageSearchResults([]);
    } finally {
      setImageSearchLoading(false);
    }
  };

  const handleClearImageSearch = () => {
    setImageSearchResults(null);
    setImageSearchPreview(null);
    setImageSearchError(null);
    setImageSearchLoading(false);
    if (imageSearchPreview) URL.revokeObjectURL(imageSearchPreview);
  };

  // Determine which wheels to render in the grid
  const inImageSearchMode = imageSearchResults !== null || imageSearchLoading;
  const displayWheels = inImageSearchMode ? (imageSearchResults || []) : wheels;

  return (
    <div className="wheellibrary">
      {/* Filters bar */}
      <div className="wheellibrary-filters">
        <div className="wheellibrary-filters-row">
          <button
            className="wheellibrary-new-btn"
            onClick={() => setShowNewModal(true)}
          >
            + New Wheel
          </button>

          <input
            className="wheellibrary-search"
            type="text"
            placeholder="Search Wheels"
            value={searchQuery}
            onChange={handleSearchChange}
            disabled={inImageSearchMode}
          />

          {/* Image search button */}
          {!inImageSearchMode ? (
            <button
              className="wheellibrary-imgsearch-btn"
              onClick={handleImageSearchClick}
              title="Search by photo"
            >
              <span className="wheellibrary-imgsearch-icon">📷</span>
              <span className="wheellibrary-imgsearch-label">Search by Photo</span>
            </button>
          ) : (
            <button
              className="wheellibrary-imgsearch-clear-btn"
              onClick={handleClearImageSearch}
              title="Clear image search"
            >
              ✕ Clear
            </button>
          )}

          {/* Hidden file input — accepts images from camera or file picker */}
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: 'none' }}
            onChange={handleImageSearchFileChange}
          />
        </div>

        {/* Image search status bar */}
        {inImageSearchMode && (
          <div className="wheellibrary-imgsearch-bar">
            {imageSearchPreview && (
              <img
                src={imageSearchPreview}
                alt="Search query"
                className="wheellibrary-imgsearch-preview"
              />
            )}
            <span className="wheellibrary-imgsearch-status">
              {imageSearchLoading
                ? 'Searching for similar wheels...'
                : imageSearchError
                ? imageSearchError
                : `${imageSearchResults ? imageSearchResults.length : 0} likely match${
                    imageSearchResults && imageSearchResults.length !== 1 ? 'es' : ''
                  } found`}
            </span>
          </div>
        )}
      </div>

      {/* Grid */}
      <div className="wheellibrary-scroll">
        {(isLoading && !inImageSearchMode) || imageSearchLoading ? (
          <div className="wheellibrary-loading">
            {imageSearchLoading ? 'Analyzing image...' : 'Loading...'}
          </div>
        ) : displayWheels.length === 0 ? (
          <div className="wheellibrary-empty">
            {inImageSearchMode
              ? imageSearchError
                ? imageSearchError
                : 'No similar wheels found. Try a different photo.'
              : searchQuery
              ? 'No wheels match your search.'
              : 'No wheels yet. Click "+ New Wheel" to add one.'}
          </div>
        ) : (
          <div className="wheellibrary-grid">
            {inImageSearchMode && (
              <div className="wheellibrary-results-header">
                Likely Matches
              </div>
            )}
            {displayWheels.map((wheel) => {
              const imgUrl = getPrimaryImageUrl(wheel);
              const matchScore = wheel.match_score;
              return (
                <div
                  key={wheel.id}
                  className={`wheellibrary-item${inImageSearchMode ? ' wheellibrary-item--match' : ''}`}
                  onClick={() => handleWheelClick(wheel)}
                >
                  <div className="wheellibrary-photo">
                    {imgUrl && !failedImages.has(wheel.id) ? (
                      <img
                        src={imgUrl}
                        alt={wheel.name || `Wheel #${wheel.id}`}
                        className="wheellibrary-img"
                        loading="lazy"
                        onError={() =>
                          setFailedImages((prev) => new Set([...prev, wheel.id]))
                        }
                      />
                    ) : (
                      <div className="wheellibrary-no-photo">No Photo</div>
                    )}
                    {matchScore !== undefined && (
                      <div className="wheellibrary-match-badge">
                        {Math.round(matchScore * 100)}%
                      </div>
                    )}
                  </div>
                  <div className="wheellibrary-details">
                    {wheel.name && (
                      <div className="wheellibrary-name">{wheel.name}</div>
                    )}
                    {wheel.paint_color && (
                      <div className="wheellibrary-color">{wheel.paint_color}</div>
                    )}
                    {wheel.paint_code && (
                      <div className="wheellibrary-code">{wheel.paint_code}</div>
                    )}
                    {wheel.part_number && (
                      <div className="wheellibrary-part">#{wheel.part_number}</div>
                    )}
                    {!wheel.name && !wheel.paint_color && !wheel.paint_code && !wheel.part_number && (
                      <div className="wheellibrary-id">Wheel #{wheel.id}</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Floating add button (mobile) */}
      <button
        className="wheellibrary-mobile-btn"
        onClick={() => setShowNewModal(true)}
        aria-label="Add Wheel"
      >
        +
      </button>

      {/* Modals */}
      <WheelModal
        showModal={showModal}
        toggleModal={handleModalClose}
        wheel={selectedWheel}
        onWheelUpdated={handleWheelUpdated}
        onWheelDeleted={handleWheelDeleted}
      />

      <NewWheelModal
        showModal={showNewModal}
        toggleModal={() => setShowNewModal(false)}
        onWheelCreated={handleWheelCreated}
      />
    </div>
  );
};

export default WheelLibrary;
